import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";

export default function RoleAdd({ createRole, onRoleAdded }) {
  const { t } = useTranslation();
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Notification states
  // const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Get organization ID from localStorage
  const [organizationId, setOrganizationId] = useState(null);

  useEffect(() => {
    try {
      const userState = localStorage.getItem("userState");
      if (userState) {
        const parsedUserState = JSON.parse(userState);
        if (parsedUserState?.user?.organizationId) {
          setOrganizationId(parsedUserState.user.organizationId);
        }
      }
    } catch (error) {
      console.error("Error parsing userState from localStorage:", error);
    }
  }, []);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error for this field when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: "",
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate fields
    let errors = {};
    if (!formData.name) {
      errors.name = "Role name is required";
    }
    if (!formData.description) {
      errors.description = "Description is required";
    }
    setFormErrors(errors);

    // Check if any errors exist
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    const newRole = {
      name: formData.name,
      description: formData.description,
      organizationId: organizationId,
    };



    try {
      const result = await createRole(newRole);
      console.log("Role created successfully:", result);

      // Reset form fields
      setFormData({
        name: "",
        description: "",
      });

      // Display success message
      setSuccessMessage("Role created successfully!");
      // setSnackbarOpen(true);

      // Notify parent component
      if (onRoleAdded) {
        onRoleAdded();
      }
    } catch (error) {
      console.error("Error creating role:", error);

      // Display error message
      setErrorMessage("Failed to create role. Please try again.");
      // setSnackbarOpen(true);

      // Clear success message
      setSuccessMessage("");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    // setSnackbarOpen(false);
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      style={{
        minHeight: "238px",
        position: "relative",
        borderRadius: 10,
        borderWidth: 1,
        padding: "40px",
        margin: "0px 40px",
        gap: 20,
        border: "1px solid #A5DCF3",
        boxShadow: "-2px 2px 5px 0px #00000040",
      }}
    >
      <Box
        sx={{
          height: 52,
          borderRadius: "8px",
          gap: 2,
          px: 3,
          py: 1.5,
          background: "#647490",
          boxShadow: "-2px 2px 5px 0px #00000040",
          fontFamily: "Roboto",
          fontWeight: 600,
          fontSize: {
            xs: "16px",
            sm: "18px",
            md: "20px",
            lg: "24px",
          },
          lineHeight: "100%",
          color: "#FFFFFF",
        }}
      >
        {t("Create a New Role")}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "flex-end" },
          justifyContent: { xs: "center", md: "space-between" },
          width: "100%",
          marginTop: "20px",
          gap: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
            width: { xs: "100%", md: "auto" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: { xs: "100%", md: 400 },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                marginBottom: "8px",
                fontWeight: 500,
                color: "#1F242E",
                fontFamily: "Roboto",
                fontSize: { xs: "18px", md: "20px" },
                lineHeight: "100%",
              }}
            >
              {t("role")}
              <Typography
                component="span"
                sx={{ color: "red", marginLeft: "4px" }}
              >
                *
              </Typography>
            </Typography>
            <TextField
              placeholder={t("Enter the role (e.g.Admin)")}
              variant="outlined"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              error={!!formErrors.name}
              helperText={formErrors.name}
              InputLabelProps={{ shrink: false }}
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  height: 55,
                  borderRadius: "16px",
                  borderWidth: 2,
                  "& fieldset": {
                    borderWidth: "2px",
                    borderColor: "rgba(24, 48, 89, 1)",
                  },
                  "&.Mui-error fieldset": {
                    borderColor: "rgba(24, 48, 89, 1)",
                  },
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: { xs: "100%", md: 400 },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                marginBottom: "8px",
                fontWeight: 500,
                color: "#1F242E",
                fontFamily: "Roboto",
                fontSize: { xs: "18px", md: "20px" },
                lineHeight: "100%",
              }}
            >
              {t("Description")}
              <Typography
                component="span"
                sx={{ color: "red", marginLeft: "4px" }}
              >
                *
              </Typography>
            </Typography>
            <TextField
              placeholder={t(
                "Enter role description (e.g.Manages user accounts)"
              )}
              variant="outlined"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              error={!!formErrors.description}
              helperText={formErrors.description}
              InputLabelProps={{ shrink: false }}
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  height: 55,
                  borderRadius: "16px",
                  borderWidth: 2,
                  "& fieldset": {
                    borderWidth: "2px",
                    borderColor: "rgba(24, 48, 89, 1)",
                  },
                  "&.Mui-error fieldset": {
                    borderColor: "rgba(24, 48, 89, 1)",
                  },
                },
              }}
            />
          </Box>
        </Box>

        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          sx={{
            width: { xs: "100%", sm: 150, md: 112 },
            height: 55,
            borderRadius: "8px",
            padding: "16px 24px",
            border: "1px solid #414958",
            backgroundColor: "#F9E719",
            fontFamily: "Roboto",
            fontWeight: 600,
            fontSize: { xs: "18px", md: "20px" },
            lineHeight: "100%",
            marginTop: { xs: 2, md: 0 },
            color: "#1F242E",
            "&:hover": {
              backgroundColor: "#e0cf16",
            },
            "&:disabled": {
              backgroundColor: "#f9e71980",
              color: "#1f242e80",
            },
            textTransform: "none",
          }}
        >
          {t("Save")}
        </Button>
      </Box>

      {/* Success/Error Snackbar */}
      {/* <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={successMessage ? "success" : "error"}
                    sx={{ width: '100%' }}
                >
                    {successMessage || errorMessage}
                </Alert>
            </Snackbar> */}
    </Box>
  );
}
